import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type RecordingStatus = 'idle' | 'recording' | 'paused' | 'stopped';

export interface RecordingMeta {
  encounterId?: string;
  finalDuration?: number;
  pausedDuration: number;
  startedAt: number | null;
  status: RecordingStatus;
}

export interface EncounterFormDraft {
  assessment?: string;
  assigned_to?: string;
  chief_complaint?: string;
  current_medications?: string;
  encounter_date?: string;
  encounter_type?: 'initial' | 'follow-up' | 'urgent' | 'telehealth';
  heart_rate?: string;
  height?: string;
  o2_saturation?: string;
  patient_id?: string;
  plan?: string;
  pressure?: string;
  temperature?: string;
  template_id?: string;
  /**
   * Optional human-readable title for the recording. Surfaced on the
   * generated document (additional_context.title) and the encounter
   * (clinical_context.title) when present.
   */
  title?: string;
  weight?: string;
}

export interface EncounterSessionState {
  formDraft: EncounterFormDraft;
  originRoute: string | null;
  recordingMeta: RecordingMeta;
  templateContext: Record<string, string>;
}

export interface EncounterSessionActions {
  clearSession: () => void;
  setFormDraft: (draft: Partial<EncounterFormDraft>) => void;
  setOriginRoute: (route: string) => void;
  setRecordingMeta: (meta: Partial<RecordingMeta>) => void;
  setTemplateContext: (ctx: Record<string, string>) => void;
}

const initialRecordingMeta: RecordingMeta = {
  status: 'idle',
  startedAt: null,
  pausedDuration: 0,
  finalDuration: undefined,
  encounterId: undefined,
};

const initialState: EncounterSessionState = {
  formDraft: {},
  originRoute: null,
  recordingMeta: initialRecordingMeta,
  templateContext: {},
};

export const useEncounterSessionStore = create<EncounterSessionState & EncounterSessionActions>()(
  persist(
    (set) => ({
      ...initialState,

      setFormDraft: (draft) =>
        set((state) => ({
          formDraft: { ...state.formDraft, ...draft },
        })),

      setOriginRoute: (route) => set({ originRoute: route }),

      setRecordingMeta: (meta) =>
        set((state) => ({
          recordingMeta: { ...state.recordingMeta, ...meta },
        })),

      setTemplateContext: (ctx) => set({ templateContext: ctx }),

      clearSession: () => set(initialState),
    }),
    {
      name: 'encounter-session',
      onRehydrateStorage: () => (state) => {
        if (state && state.recordingMeta.status !== 'idle') {
          state.setRecordingMeta({
            status: 'idle',
            startedAt: null,
            pausedDuration: 0,
          });
        }
      },
    },
  ),
);
